import React, { createContext, useContext, useState, useCallback } from 'react';
import en from '../translations/en';
import fr from '../translations/fr';
import frLiterals from '../translations/frLiterals';

const TRANSLATIONS = { en, fr };

const normalizeLiteral = (value) => String(value ?? '')
  .replace(/\u00A0/g, ' ')
  .replace(/[’]/g, "'")
  .replace(/[–—]/g, '-')
  .replace(/\s+/g, ' ')
  .trim();

const FR_LITERAL_NORMALIZED = Object.entries(frLiterals).reduce((acc, [key, translated]) => {
  const normalizedKey = normalizeLiteral(key);
  if (!acc[normalizedKey]) acc[normalizedKey] = translated;
  return acc;
}, {});

const resolveFrenchLiteral = (value) => {
  if (typeof value !== 'string') return value;
  if (Object.prototype.hasOwnProperty.call(frLiterals, value)) return frLiterals[value];
  const normalized = normalizeLiteral(value);
  return FR_LITERAL_NORMALIZED[normalized] || value;
};

const FR_ACRONYM_MAP = {
  TFSA: 'CELI',
  RRSP: 'REER',
  FHSA: 'CELIAPP',
  RESP: 'REEE',
  RRIF: 'FERR',
  CPP: 'RPC',
  OAS: 'SV',
  GIS: 'SRG',
};

const FR_WORD_FIXES = {
  educatif: 'éducatif',
  educatifs: 'éducatifs',
  educative: 'éducative',
  educatives: 'éducatives',
  systeme: 'système',
  systemes: 'systèmes',
  invite: 'invité',
  invites: 'invités',
  connecte: 'connecté',
  connectes: 'connectés',
  deconnecte: 'déconnecté',
  deconnecter: 'déconnecter',
  deconnexion: 'déconnexion',
  education: 'éducation',
  modelisation: 'modélisation',
  modeliser: 'modéliser',
  modelisez: 'modélisez',
  modeles: 'modèles',
  modele: 'modèle',
  scenarios: 'scénarios',
  scenario: 'scénario',
  financiere: 'financière',
  financieres: 'financières',
  clarte: 'clarté',
  menages: 'ménages',
  menage: 'ménage',
  regles: 'règles',
  regle: 'règle',
  verifier: 'vérifier',
  verifie: 'vérifie',
  verifiez: 'vérifiez',
  detaille: 'détaillé',
  detaillee: 'détaillée',
  detaillees: 'détaillées',
  cles: 'clés',
  echeance: 'échéance',
  echeances: 'échéances',
  regime: 'régime',
  litteratie: 'littératie',
  cumules: 'cumulés',
  deduction: 'déduction',
  deductions: 'déductions',
  deductible: 'déductible',
  deductibles: 'déductibles',
  dependent: 'dépendent',
  depend: 'dépend',
  eleve: 'élevé',
  eleves: 'élevés',
  elevee: 'élevée',
  elevees: 'élevées',
  bibliotheque: 'bibliothèque',
  preparation: 'préparation',
  epargne: 'épargne',
  annees: 'années',
  annee: 'année',
  age: 'âge',
  admissibilite: 'admissibilité',
  repartition: 'répartition',
  interet: 'intérêt',
  composee: 'composée',
  compose: 'composé',
  specialisees: 'spécialisées',
  specialisee: 'spécialisée',
  specialise: 'spécialisé',
  facon: 'façon',
  impot: 'impôt',
  impots: 'impôts',
  resultats: 'résultats',
  resultat: 'résultat',
  hypotheses: 'hypothèses',
  hypothese: 'hypothèse',
  conformite: 'conformité',
  methodologie: 'méthodologie',
  experience: 'expérience',
  francais: 'français',
  concu: 'conçu',
  enregistres: 'enregistrés',
  enregistrees: 'enregistrées',
  enregistree: 'enregistrée',
  enregistre: 'enregistré',
  donnees: 'données',
  donnee: 'donnée',
  volatilite: 'volatilité',
  metriques: 'métriques',
  metrique: 'métrique',
  activites: 'activités',
  activite: 'activité',
  securite: 'sécurité',
  parametres: 'paramètres',
  parametre: 'paramètre',
  qualite: 'qualité',
  duree: 'durée',
  retiree: 'retirée',
  retirees: 'retirées',
  estimee: 'estimée',
  estimees: 'estimées',
  estime: 'estimé',
  estimes: 'estimés',
  reequilibrage: 'rééquilibrage',
  decaissement: 'décaissement',
  benefices: 'bénéfices',
  benefice: 'bénéfice',
  capacite: 'capacité',
  liquidite: 'liquidité',
  boursiere: 'boursière',
  boursieres: 'boursières',
  premiere: 'première',
  premieres: 'premières',
  etudes: 'études',
  etude: 'étude',
  cote: 'côté',
};

const FR_PHRASE_FIXES = [
  [/\bsappliquent\b/gi, "s'appliquent"],
  [/\blimpot\b/gi, "l'impôt"],
  [/\bdimpot\b/gi, "d'impôt"],
  [/\bdutilisation\b/gi, "d'utilisation"],
  [/\bdouverture\b/gi, "d'ouverture"],
  [/\bdachat\b/gi, "d'achat"],
  [/\bdage\b/gi, "d'âge"],
  [/\bdetudes\b/gi, "d'études"],
  [/\bladmissibilite\b/gi, "l'admissibilité"],
  [/\blannee\b/gi, "l'année"],
  [/\bdurgence\b/gi, "d'urgence"],
  [/\blimpact\b/gi, "l'impact"],
  [/\bdinvestissement\b/gi, "d'investissement"],
  [/\bdincertitude\b/gi, "d'incertitude"],
  [/\bdobjectif\b/gi, "d'objectif"],
  [/\bdobjectifs\b/gi, "d'objectifs"],
  [/\bmise a jour\b/gi, 'mise à jour'],
  [/\ba ete\b/gi, 'a été'],
  [/\ba long terme\b/gi, 'à long terme'],
  [/a la fois/gi, 'à la fois'],
  [/a des fins/gi, 'à des fins'],
  [/a partir/gi, 'à partir'],
];

const toCaseMatched = (match, replacement) => {
  if (match === match.toUpperCase()) {
    return replacement.toUpperCase();
  }
  if (match[0] === match[0].toUpperCase()) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1);
  }
  return replacement;
};

const polishFrenchText = (value) => {
  if (typeof value !== 'string') {
    return value;
  }

  let result = value;

  for (const [regex, replacement] of FR_PHRASE_FIXES) {
    result = result.replace(regex, replacement);
  }

  // Restore common elisions where a missing apostrophe hurts readability.
  result = result.replace(/\b([cdjlmnst])\s+([aeiouh])/gi, "$1'$2");

  for (const [source, target] of Object.entries(FR_WORD_FIXES)) {
    const regex = new RegExp(`\\b${source}\\b`, 'gi');
    result = result.replace(regex, (match) => toCaseMatched(match, target));
  }

  result = result.replace(/\b(TFSA|RRSP|FHSA|RESP|RRIF|CPP|OAS|GIS)\b/g, (acronym) => FR_ACRONYM_MAP[acronym] || acronym);

  return result;
};

const LanguageContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: (key) => key,
  tt: (text) => text,
});

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem('preferredLang') || 'en';
    } catch {
      return 'en';
    }
  });

  const changeLang = useCallback((newLang) => {
    setLang(newLang);
    try {
      localStorage.setItem('preferredLang', newLang);
    } catch {}
  }, []);

  const t = useCallback((key, fallback) => {
    const dict = TRANSLATIONS[lang] || en;
    const translated = dict[key];
    if (translated !== undefined) {
      return lang === 'fr' ? polishFrenchText(translated) : translated;
    }

    const value = fallback ?? key;
    if (lang === 'fr' && typeof value === 'string') {
      return polishFrenchText(resolveFrenchLiteral(value));
    }
    return value;
  }, [lang]);

  const tt = useCallback((text) => {
    if (lang !== 'fr') return text;
    return polishFrenchText(resolveFrenchLiteral(text));
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang: changeLang, t, tt }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

export default LanguageContext;
